// Eine Liste von Einträgen, die das Protokoll Stand für Stand liefert und die
// beim Start (und nach Aktionen) zusätzlich per REST geholt wird -- die
// gemeinsame Mechanik der Summary-Stores, die ihre REST-Form aus Protokoll-
// Ständen bauen (steckdosen.svelte.ts, acinfinity-summary.svelte.ts; dasselbe
// wie agents-summary.svelte.ts, nur als Klasse).
//
// Die Regeln:
//   - Ein Stand aktualisiert den Eintrag IN-PLACE (Objekt-Identität bleibt,
//     nur geänderte Felder werden gesetzt); ein unbekannter kommt an seinen
//     Platz der Reihenfolge, sobald die Liste „bereit“ ist (vorher legt der
//     erste Abruf ihn über die Antwort); `weg` nimmt ihn heraus.
//   - Der jüngste Stand je Eintrag wird mit laufender Nummer gemerkt: kam er
//     während eines REST-Abrufs, liegt er über dessen Antwort (sie kann
//     dahinter liegen), ältere nicht. Ein `weg` hinterlässt einen Grabstein,
//     damit eine Antwort, die den Eintrag noch nennt, ihn nicht zurückholt.

import { mitStaenden, uebernimm, type Gemerkt } from "./storage-protokoll";

export class ProtokollListe<T extends object> {
  /** Die Einträge in der Reihenfolge von `vergleiche`; tief reaktiv. */
  items = $state<T[]>([]);
  /** Laufende Nummer des jüngsten Stands (für `mische`: Nummer vor dem Abruf merken). */
  nr = 0;
  /** Ob ein Stand für einen unbekannten Eintrag diesen anlegen darf (nach dem ersten Abruf). */
  bereit = false;

  private readonly merk = new Map<string, Gemerkt<T>>();
  private readonly schluessel: (e: T) => string;
  private readonly vergleiche: (a: T, b: T) => number;

  constructor(schluessel: (e: T) => string, vergleiche: (a: T, b: T) => number) {
    this.schluessel = schluessel;
    this.vergleiche = vergleiche;
  }

  /** Die Liste in der Reihenfolge von `vergleiche`; nur bei Abweichung neu
   *  gesetzt (die Einträge behalten ihre Identität). */
  ordne(): void {
    const next = [...this.items].sort(this.vergleiche);
    if (next.some((e, i) => e !== this.items[i])) this.items = next;
  }

  /** Ein Stand für einen Eintrag. true, wenn sich die Liste geändert hat. */
  stand(neu: T): boolean {
    const k = this.schluessel(neu);
    this.merk.set(k, { e: neu, nr: ++this.nr });
    const ex = this.items.find((x) => this.schluessel(x) === k);
    if (ex) {
      if (!uebernimm(ex, neu)) return false;
      this.ordne();
      return true;
    }
    if (!this.bereit) return false;
    this.items.push(neu);
    this.ordne();
    return true;
  }

  /** Der Eintrag ist weg (Grabstein). true, wenn die Liste ihn führte. */
  weg(k: string): boolean {
    this.merk.set(k, { e: null, nr: ++this.nr });
    const i = this.items.findIndex((x) => this.schluessel(x) === k);
    if (i < 0) return false;
    this.items.splice(i, 1);
    return true;
  }

  /** Gab es seit `ab` einen Stand? */
  neuer(ab: number): boolean {
    return this.nr > ab;
  }

  /** Die frische Antwort (oder, wenn der Abruf scheiterte, `undefined`) plus die
   *  Stände, die während des Abrufs kamen (Nummer > `ab`), IN-PLACE in die
   *  Liste: vorhandene Einträge behalten Objekt-Identität, die Reihenfolge
   *  bestimmt `vergleiche`. true, wenn sich die Liste geändert hat. */
  mische(rest: T[] | undefined, ab: number): boolean {
    if (!rest && !this.neuer(ab)) return false;
    const alle = mitStaenden(rest ?? this.items, this.merk, ab, this.schluessel);
    const vorhanden = new Map(this.items.map((e) => [this.schluessel(e), e]));
    const next = alle.map((e) => {
      const ex = vorhanden.get(this.schluessel(e));
      if (ex) {
        uebernimm(ex, e);
        return ex;
      }
      return e;
    });
    next.sort(this.vergleiche);
    const same = next.length === this.items.length && next.every((e, i) => e === this.items[i]);
    if (!same) this.items = next;
    return !same;
  }

  /** Ob der Stand zum Schlüssel jünger ist als `ab` (kam während eines Abrufs). */
  jungerAls(k: string, ab: number): boolean {
    const g = this.merk.get(k);
    return g !== undefined && g.nr > ab;
  }

  /** Alles zurücksetzen (Stopp des Stores). */
  leere(): void {
    this.items = [];
    this.merk.clear();
    this.bereit = false;
  }
}
